import {
  Circle,
  CircleCheck,
  CircleDollarSign,
  Radio,
  Server,
} from "lucide-react";
import type { ComponentType, SVGProps } from "react";

import { cx, StatusPill } from "../../design/primitives";

export type RailState =
  | "idle"
  | "active"
  | "complete"
  | "error"
  | "revoked";

export type ExecutionStep = {
  id: "request" | "provider" | "stream" | "result" | "usage";
  label: string;
  detail: string;
  state: RailState;
};

const stateLabel: Record<RailState, string> = {
  idle: "În așteptare",
  active: "În lucru",
  complete: "Finalizat",
  error: "Eroare",
  revoked: "Revocat",
};

const iconByStep: Record<
  ExecutionStep["id"],
  ComponentType<SVGProps<SVGSVGElement>>
> = {
  request: Circle,
  provider: Server,
  stream: Radio,
  result: CircleCheck,
  usage: CircleDollarSign,
};

export const ExecutionRail = ({
  steps,
  status = "Nicio rulare activă",
  statusTone = "neutral",
}: {
  steps: ExecutionStep[];
  status?: string;
  statusTone?: "neutral" | "info" | "success" | "warning" | "error";
}) => (
  <section className="execution-rail" aria-labelledby="execution-rail-title">
    <div className="execution-rail__header">
      <div>
        <p className="eyebrow">Controlul rulării</p>
        <h3 id="execution-rail-title">Traseul de execuție</h3>
      </div>
      <StatusPill
        tone={statusTone}
        pulse={steps.some((step) => step.state === "active")}
      >
        {status}
      </StatusPill>
    </div>
    <ol className="execution-rail__steps">
      {steps.map((step) => {
        const Icon = iconByStep[step.id];
        return (
          <li
            key={step.id}
            className={cx(
              "execution-step",
              `execution-step--${step.state}`,
            )}
            aria-current={step.state === "active" ? "step" : undefined}
          >
            <div className="execution-step__node" aria-hidden="true">
              <Icon />
            </div>
            <div className="execution-step__copy">
              <span className="execution-step__label">{step.label}</span>
              <span className="execution-step__detail">{step.detail}</span>
              <span className="sr-only">{stateLabel[step.state]}</span>
            </div>
          </li>
        );
      })}
    </ol>
  </section>
);
